import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Download,
  Upload,
  ChevronDown,
  ChevronRight,
  Package,
  DollarSign,
  TrendingUp,
  BarChart3,
  Loader2,
  Ship,
  Calculator,
  GitCompare,
  ArrowLeftRight,
  X,
  CheckSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import api from '@/lib/axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * HistoricalView Component
 * Displays historical quotation data with search, filtering, and container projection features.
 */
const HistoricalView = () => {
  // Data state
  const [entries, setEntries] = useState([]);
  const [groupedEntries, setGroupedEntries] = useState({});
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Filter state
  const [searchText, setSearchText] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [m2Target, setM2Target] = useState('');
  const [m2Tolerance, setM2Tolerance] = useState('300');
  
  // UI state
  const [expandedGroups, setExpandedGroups] = useState({});
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  
  // Comparison state
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const [resyncLoading, setResyncLoading] = useState(false);
  const handleResyncAll = async () => {
    setResyncLoading(true);
    try {
      const res = await api.post(`${API}/api/historical/resync-all`);
      toast.success(res.data.message);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recalcular');
    } finally {
      setResyncLoading(false);
    }
  };

  // Fetch entries
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchText) params.append('search', searchText);
      if (selectedGroup && selectedGroup !== 'all') params.append('group_name', selectedGroup);
      if (m2Target) {
        params.append('m2_target', m2Target);
        params.append('m2_tolerance', m2Tolerance || '300');
      }
      
      const response = await api.get(`${API}/api/historical/entries?${params.toString()}`);
      const data = response.data;
      
      setEntries(data.entries || []);
      setGroups(data.groups || []);
      setTotal(data.total || 0);
      
      // Group entries by group_name
      const grouped = {};
      (data.entries || []).forEach(entry => {
        const group = entry.group_name || entry.project_name || 'Sin Grupo';
        if (!grouped[group]) {
          grouped[group] = [];
        }
        grouped[group].push(entry);
      });
      setGroupedEntries(grouped);
      
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Error al cargar datos históricos');
    } finally {
      setLoading(false);
    }
  }, [searchText, selectedGroup, m2Target, m2Tolerance]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get(`${API}/api/historical/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Toggle entry selection for comparison
  const toggleCompareSelection = (entry) => {
    setSelectedForCompare(prev => {
      const exists = prev.find(e => e.id === entry.id);
      if (exists) {
        return prev.filter(e => e.id !== entry.id);
      }
      if (prev.length >= 2) {
        // Replace the first selection
        return [prev[1], entry];
      }
      return [...prev, entry];
    });
  };

  // Clear comparison selection
  const clearCompareSelection = () => {
    setSelectedForCompare([]);
  };

  // Calculate comparison differences
  const comparisonData = useMemo(() => {
    if (selectedForCompare.length !== 2) return null;
    
    const [entry1, entry2] = selectedForCompare;
    
    const calcDiff = (val1, val2) => {
      if (val1 === 0 && val2 === 0) return { diff: 0, percent: 0 };
      if (val1 === 0) return { diff: val2, percent: 100 };
      const diff = val2 - val1;
      const percent = ((diff / val1) * 100).toFixed(1);
      return { diff, percent };
    };
    
    return {
      m2: calcDiff(entry1.m2 || 0, entry2.m2 || 0),
      fob_real: calcDiff(entry1.fob_real || 0, entry2.fob_real || 0),
      fob_excel: calcDiff(entry1.fob_excel || 0, entry2.fob_excel || 0),
      cost_per_m2_chi: calcDiff(entry1.cost_per_m2_chi || 0, entry2.cost_per_m2_chi || 0),
      venta_mas_igv: calcDiff(entry1.venta_mas_igv || 0, entry2.venta_mas_igv || 0),
      cost_per_m2_igv: calcDiff(entry1.cost_per_m2_igv || 0, entry2.cost_per_m2_igv || 0),
      flete: calcDiff(entry1.flete || 0, entry2.flete || 0),
      nacionalizacion: calcDiff(entry1.nacionalizacion || 0, entry2.nacionalizacion || 0),
      logistico: calcDiff(entry1.logistico || 0, entry2.logistico || 0),
    };
  }, [selectedForCompare]);

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, [fetchEntries]);

  // Import Excel data
  const handleImportExcel = async () => {
    setImportLoading(true);
    try {
      const response = await api.post(`${API}/api/historical/import-excel`);
      toast.success(response.data.message);
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error(error.response?.data?.detail || 'Error al importar datos');
    } finally {
      setImportLoading(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (selectedGroup && selectedGroup !== 'all') params.append('group_name', selectedGroup);
      if (m2Target) {
        params.append('m2_target', m2Target);
        params.append('m2_tolerance', m2Tolerance || '300');
      }
      
      const response = await api.get(`${API}/api/historical/export-excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historico_alumetric_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel exportado correctamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar a Excel');
    }
  };

  // Toggle group expansion
  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Expand all groups
  const expandAll = () => {
    const allExpanded = {};
    Object.keys(groupedEntries).forEach(group => {
      allExpanded[group] = true;
    });
    setExpandedGroups(allExpanded);
  };

  // Collapse all groups
  const collapseAll = () => {
    setExpandedGroups({});
  };

  // Save entry (create or update)
  const handleSaveEntry = async (entryData) => {
    try {
      if (editingEntry) {
        await api.put(`${API}/api/historical/entries/${editingEntry.id}`, entryData);
        toast.success('Entrada actualizada');
      } else {
        await api.post(`${API}/api/historical/entries`, entryData);
        toast.success('Entrada creada');
      }
      setShowEditModal(false);
      setShowAddModal(false);
      setEditingEntry(null);
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  // Delete entry
  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`¿Eliminar la entrada ${entry.quotation_code}?`)) return;
    
    try {
      await api.delete(`${API}/api/historical/entries/${entry.id}`);
      toast.success('Entrada eliminada');
      fetchEntries();
      fetchStats();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value == null) return '-';
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value == null) return '-';
    return `${(value * 100).toFixed(2)}%`;
  };

  // Format number
  const formatNumber = (value, decimals = 2) => {
    if (value == null) return '-';
    return value.toLocaleString('es-PE', { maximumFractionDigits: decimals });
  };

  // Render entry row
  const renderEntryRow = (entry, isGrouped = false) => {
    const isSelectedForCompare = selectedForCompare.some(e => e.id === entry.id);
    
    return (
      <TableRow 
        key={entry.id} 
        className={`${isGrouped ? 'bg-slate-50/50' : ''} ${isSelectedForCompare ? 'bg-purple-50 border-l-4 border-purple-500' : ''} hover:bg-blue-50/50`}
      >
        <TableCell className="text-center">
          <Checkbox
            checked={isSelectedForCompare}
            onCheckedChange={() => toggleCompareSelection(entry)}
            className="data-[state=checked]:bg-purple-600"
            data-testid={`compare-checkbox-${entry.id}`}
          />
        </TableCell>
        <TableCell className="font-mono text-xs">{entry.quotation_code}</TableCell>
        <TableCell className="font-medium">{entry.project_name}</TableCell>
        <TableCell className="text-right font-medium">{formatNumber(entry.total_m2)}</TableCell>
        <TableCell className="text-right">{formatCurrency(entry.fob_real)}</TableCell>
        <TableCell className="text-right">{formatCurrency(entry.cost_per_m2_chi)}</TableCell>
        <TableCell className="text-center">
          {entry.container_count != null ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              <Ship className="w-3 h-3" />
              {entry.container_count}
            </span>
          ) : '-'}
        </TableCell>
        <TableCell className="text-right">{formatCurrency(entry.fob_excel)}</TableCell>
        <TableCell className={`text-right ${
          entry.fob_percentage != null 
            ? entry.fob_percentage < 1 
              ? 'text-green-600 font-medium' 
              : entry.fob_percentage > 1.1 
                ? 'text-red-600 font-medium' 
                : 'text-slate-600'
            : ''
        }`}>
          {formatPercent(entry.fob_percentage)}
        </TableCell>
        <TableCell className="text-right">{formatCurrency(entry.sale_with_igv)}</TableCell>
        <TableCell className="text-right">{formatCurrency(entry.sale_per_m2_igv)}</TableCell>
        <TableCell className="text-right">{formatCurrency(entry.sale_per_m2_no_igv)}</TableCell>
        <TableCell className="text-right">{formatCurrency(entry.freight_cost)}</TableCell>
        <TableCell className="text-right">{formatCurrency(entry.nationalization_cost)}</TableCell>
        <TableCell className="text-right">{formatCurrency(entry.logistics_cost)}</TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingEntry(entry);
                setShowEditModal(true);
              }}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteEntry(entry)}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Histórico de Proyectos</h2>
          <p className="text-sm text-slate-500">
            {total} registros • {Object.keys(groupedEntries).length} proyectos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStatsModal(true)}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Estadísticas
          </Button>
          <Button
            variant="outline"
            onClick={handleExportExcel}
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
          <Button
            variant="outline"
            onClick={handleImportExcel}
            disabled={importLoading}
          >
            {importLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Importar Excel
          </Button>
          <Button
            variant="outline"
            onClick={handleResyncAll}
            disabled={resyncLoading}
            title="Recalcular Venta+IGV y M² de las entradas vinculadas a cotizaciones (solo Suministro + IGV)"
            data-testid="historical-resync-btn"
          >
            {resyncLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Recalcular
          </Button>
          <Button
            onClick={() => {
              setEditingEntry(null);
              setShowAddModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Entrada
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-white rounded-lg border">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Buscar Código/Proyecto</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="MV-001... o BOHEM..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Filtrar por Proyecto</Label>
          <Select value={selectedGroup} onValueChange={setSelectedGroup}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los proyectos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los proyectos</SelectItem>
              {groups.map(group => (
                <SelectItem key={group} value={group}>{group}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">
            <Calculator className="w-3 h-3 inline mr-1" />
            Buscar por M² (aproximado)
          </Label>
          <Input
            type="number"
            placeholder="Ej: 1500"
            value={m2Target}
            onChange={(e) => setM2Target(e.target.value)}
          />
        </div>
        
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Tolerancia M²</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="300"
              value={m2Tolerance}
              onChange={(e) => setM2Tolerance(e.target.value)}
              className="w-24"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={fetchEntries}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Filter className="w-4 h-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchText('');
                setSelectedGroup('all');
                setM2Target('');
                setM2Tolerance('300');
              }}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* M2 Search Info */}
      {m2Target && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <Ship className="w-4 h-4 inline mr-2" />
            Mostrando proyectos con <strong>{formatNumber(parseFloat(m2Target) - parseFloat(m2Tolerance || 300))} - {formatNumber(parseFloat(m2Target) + parseFloat(m2Tolerance || 300))} M²</strong> para proyección de contenedores
          </p>
        </div>
      )}

      {/* Expand/Collapse buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={expandAll}>
            <ChevronDown className="w-4 h-4 mr-1" />
            Expandir todo
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            <ChevronRight className="w-4 h-4 mr-1" />
            Colapsar todo
          </Button>
        </div>
        
        {/* Comparison Bar */}
        {selectedForCompare.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg">
            <GitCompare className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">
              {selectedForCompare.length}/2 seleccionados para comparar
            </span>
            <div className="flex gap-1">
              {selectedForCompare.map((entry, idx) => (
                <Badge key={entry.id} variant="secondary" className="text-xs">
                  {entry.codigo}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={clearCompareSelection}
                className="text-purple-600 hover:text-purple-800"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCompareModal(true)}
                disabled={selectedForCompare.length !== 2}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                Comparar
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Table with sticky header */}
      <div className="bg-white rounded-lg border">
        <div 
          className="overflow-auto max-h-[calc(100vh-350px)]" 
          data-testid="historical-table-container"
          style={{ position: 'relative' }}
        >
          <table className="w-full caption-bottom text-sm">
            <thead className="sticky top-0 z-20 bg-slate-100" style={{ position: 'sticky', top: 0 }}>
              <tr className="border-b hover:bg-slate-100">
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-[50px] bg-slate-100">
                  <GitCompare className="w-4 h-4 mx-auto text-slate-400" title="Seleccionar para comparar" />
                </th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-[110px] bg-slate-100">Código</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-[150px] bg-slate-100">Proyecto</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[90px] bg-slate-100">M²</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[100px] bg-slate-100">FOB Real</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[90px] bg-slate-100">$/M² CHI</th>
                <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-[70px] bg-slate-100">Cont.</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[100px] bg-slate-100">FOB Excel</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[80px] bg-slate-100">% FOB</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[110px] bg-slate-100">Venta+IGV</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[90px] bg-slate-100">$/M²+IGV</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[90px] bg-slate-100">$/M²</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[90px] bg-slate-100">Flete</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[90px] bg-slate-100">Nacional.</th>
                <th className="h-10 px-2 text-right align-middle font-medium text-muted-foreground w-[90px] bg-slate-100">Logístico</th>
                <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground w-[80px] bg-slate-100">Acciones</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    <p className="text-sm text-slate-500 mt-2">Cargando...</p>
                  </TableCell>
                </TableRow>
              ) : Object.keys(groupedEntries).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={15} className="text-center py-8">
                    <Package className="w-8 h-8 mx-auto text-slate-300" />
                    <p className="text-sm text-slate-500 mt-2">No hay datos históricos</p>
                    <Button
                      variant="link"
                      onClick={handleImportExcel}
                      className="mt-2"
                    >
                      Importar desde Excel
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                Object.entries(groupedEntries).map(([groupName, groupEntries]) => (
                  <React.Fragment key={groupName}>
                    {/* Group header - collapsible */}
                    <TableRow className="bg-slate-200/50 cursor-pointer hover:bg-slate-200" onClick={() => toggleGroup(groupName)}>
                      <TableCell colSpan={16}>
                        <div className="flex items-center gap-2">
                          {expandedGroups[groupName] ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span className="font-semibold text-slate-700">{groupName}</span>
                          <span className="text-xs text-slate-500 bg-slate-300 px-2 py-0.5 rounded-full">
                            {groupEntries.length} {groupEntries.length === 1 ? 'versión' : 'versiones'}
                          </span>
                          {/* Summary info */}
                          {groupEntries.some(e => e.container_count) && (
                            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Ship className="w-3 h-3" />
                              {Math.max(...groupEntries.filter(e => e.container_count).map(e => e.container_count))} cont. máx
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    
                    {/* Group entries */}
                    {expandedGroups[groupName] && groupEntries.map(entry => renderEntryRow(entry, true))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Add Modal */}
      <EntryFormModal
        isOpen={showEditModal || showAddModal}
        onClose={() => {
          setShowEditModal(false);
          setShowAddModal(false);
          setEditingEntry(null);
        }}
        onSave={handleSaveEntry}
        entry={editingEntry}
        isEditing={!!editingEntry}
      />

      {/* Stats Modal */}
      <StatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        stats={stats}
      />

      {/* Comparison Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-purple-600" />
              Comparación de Proyectos
            </DialogTitle>
            <DialogDescription>
              Comparando {selectedForCompare[0]?.quotation_code} vs {selectedForCompare[1]?.quotation_code}
            </DialogDescription>
          </DialogHeader>
          
          {selectedForCompare.length === 2 && comparisonData && (
            <div className="space-y-6 py-4">
              {/* Project Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 font-medium">PROYECTO A</p>
                  <p className="font-bold text-lg">{selectedForCompare[0].quotation_code}</p>
                  <p className="text-sm text-slate-600">{selectedForCompare[0].project_name}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium">PROYECTO B</p>
                  <p className="font-bold text-lg">{selectedForCompare[1].quotation_code}</p>
                  <p className="text-sm text-slate-600">{selectedForCompare[1].project_name}</p>
                </div>
              </div>

              {/* Comparison Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3 font-medium">Métrica</th>
                      <th className="text-right p-3 font-medium text-blue-700">{selectedForCompare[0].quotation_code}</th>
                      <th className="text-right p-3 font-medium text-purple-700">{selectedForCompare[1].quotation_code}</th>
                      <th className="text-right p-3 font-medium">Diferencia</th>
                      <th className="text-right p-3 font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <ComparisonRow 
                      label="Área Total (M²)" 
                      val1={selectedForCompare[0].total_m2} 
                      val2={selectedForCompare[1].total_m2}
                      diff={comparisonData.m2}
                      format="number"
                    />
                    <ComparisonRow 
                      label="FOB Real" 
                      val1={selectedForCompare[0].fob_real} 
                      val2={selectedForCompare[1].fob_real}
                      diff={comparisonData.fob_real}
                      format="currency"
                    />
                    <ComparisonRow 
                      label="FOB Excel" 
                      val1={selectedForCompare[0].fob_excel} 
                      val2={selectedForCompare[1].fob_excel}
                      diff={comparisonData.fob_excel}
                      format="currency"
                    />
                    <ComparisonRow 
                      label="$/M² CHI" 
                      val1={selectedForCompare[0].cost_per_m2_chi} 
                      val2={selectedForCompare[1].cost_per_m2_chi}
                      diff={comparisonData.cost_per_m2_chi}
                      format="currency"
                    />
                    <ComparisonRow 
                      label="Venta + IGV" 
                      val1={selectedForCompare[0].sale_with_igv} 
                      val2={selectedForCompare[1].sale_with_igv}
                      diff={comparisonData.venta_mas_igv}
                      format="currency"
                    />
                    <ComparisonRow 
                      label="$/M² + IGV" 
                      val1={selectedForCompare[0].sale_per_m2_igv} 
                      val2={selectedForCompare[1].sale_per_m2_igv}
                      diff={comparisonData.cost_per_m2_igv}
                      format="currency"
                    />
                    <ComparisonRow 
                      label="Flete" 
                      val1={selectedForCompare[0].freight_cost} 
                      val2={selectedForCompare[1].freight_cost}
                      diff={comparisonData.flete}
                      format="currency"
                    />
                    <ComparisonRow 
                      label="Nacionalización" 
                      val1={selectedForCompare[0].nationalization_cost} 
                      val2={selectedForCompare[1].nationalization_cost}
                      diff={comparisonData.nacionalizacion}
                      format="currency"
                    />
                    <ComparisonRow 
                      label="Logístico" 
                      val1={selectedForCompare[0].logistics_cost} 
                      val2={selectedForCompare[1].logistics_cost}
                      diff={comparisonData.logistico}
                      format="currency"
                    />
                  </tbody>
                </table>
              </div>

              {/* Containers Comparison */}
              {(selectedForCompare[0].container_count || selectedForCompare[1].container_count) && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium mb-2 flex items-center gap-2">
                    <Ship className="w-4 h-4" />
                    Contenedores
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-2xl font-bold text-blue-700">{selectedForCompare[0].container_count || '-'}</span>
                      <span className="text-sm text-slate-500 ml-2">contenedores</span>
                    </div>
                    <div>
                      <span className="text-2xl font-bold text-purple-700">{selectedForCompare[1].container_count || '-'}</span>
                      <span className="text-sm text-slate-500 ml-2">contenedores</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareModal(false)}>
              Cerrar
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                clearCompareSelection();
                setShowCompareModal(false);
              }}
            >
              Nueva Comparación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Comparison Row Component for the comparison table
 */
/**
 * Get CSS color class based on difference value
 * @param {number} diffValue - The difference value to evaluate
 * @returns {string} Tailwind CSS color class
 * @example
 * getDiffColor(100)  // returns 'text-red-600' (increase)
 * getDiffColor(-50)  // returns 'text-green-600' (decrease)
 * getDiffColor(0)    // returns 'text-slate-500' (no change)
 */
const getDiffColor = (diffValue) => {
  if (diffValue > 0) return 'text-red-600';
  if (diffValue < 0) return 'text-green-600';
  return 'text-slate-500';
};

/**
 * ComparisonRow - Row component for comparing two values side-by-side
 * @component
 * @param {Object} props
 * @param {string} props.label - Label for the comparison row
 * @param {*} props.val1 - First value to compare
 * @param {*} props.val2 - Second value to compare
 * @param {Object} props.diff - Difference object with { diff, percent } properties
 * @param {string} props.format - Format type: 'currency', 'number', or default (string)
 */
const ComparisonRow = ({ label, val1, val2, diff, format }) => {
  /**
   * Format value based on format type
   * @param {*} value - Value to format
   * @returns {string} Formatted value
   */
  const formatValue = (value) => {
    if (value == null || value === '') return '-';
    if (format === 'currency') return `$${parseFloat(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (format === 'number') return parseFloat(value).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return value;
  };
  
  const diffColor = getDiffColor(diff.diff);
  const diffSign = diff.diff > 0 ? '+' : '';
  
  return (
    <tr className="hover:bg-slate-50">
      <td className="p-3 font-medium text-slate-700">{label}</td>
      <td className="p-3 text-right text-blue-700">{formatValue(val1)}</td>
      <td className="p-3 text-right text-purple-700">{formatValue(val2)}</td>
      <td className={`p-3 text-right font-medium ${diffColor}`}>
        {diff.diff !== 0 ? `${diffSign}${formatValue(diff.diff)}` : '-'}
      </td>
      <td className={`p-3 text-right font-medium ${diffColor}`}>
        {diff.percent !== 0 ? `${diffSign}${diff.percent}%` : '-'}
      </td>
    </tr>
  );
};

/**
 * Entry Form Modal Component
 */
const EntryFormModal = ({ isOpen, onClose, onSave, entry, isEditing }) => {
  const [formData, setFormData] = useState({
    quotation_code: '',
    project_name: '',
    total_m2: '',
    fob_real: '',
    container_count: '',
    fob_excel: '',
    sale_with_igv: '',
    freight_cost: '',
    nationalization_cost: '',
    logistics_cost: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) {
      setFormData({
        quotation_code: entry.quotation_code || '',
        project_name: entry.project_name || '',
        total_m2: entry.total_m2 || '',
        fob_real: entry.fob_real || '',
        container_count: entry.container_count || '',
        fob_excel: entry.fob_excel || '',
        sale_with_igv: entry.sale_with_igv || '',
        freight_cost: entry.freight_cost || '',
        nationalization_cost: entry.nationalization_cost || '',
        logistics_cost: entry.logistics_cost || '',
      });
    } else {
      setFormData({
        quotation_code: '',
        project_name: '',
        total_m2: '',
        fob_real: '',
        container_count: '',
        fob_excel: '',
        sale_with_igv: '',
        freight_cost: '',
        nationalization_cost: '',
        logistics_cost: '',
      });
    }
  }, [entry, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const data = {
        ...formData,
        total_m2: parseFloat(formData.total_m2) || 0,
        fob_real: formData.fob_real ? parseFloat(formData.fob_real) : null,
        container_count: formData.container_count ? parseInt(formData.container_count) : null,
        fob_excel: formData.fob_excel ? parseFloat(formData.fob_excel) : null,
        sale_with_igv: formData.sale_with_igv ? parseFloat(formData.sale_with_igv) : null,
        freight_cost: formData.freight_cost ? parseFloat(formData.freight_cost) : null,
        nationalization_cost: formData.nationalization_cost ? parseFloat(formData.nationalization_cost) : null,
        logistics_cost: formData.logistics_cost ? parseFloat(formData.logistics_cost) : null,
      };
      
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Entrada Histórica' : 'Nueva Entrada Histórica'}
          </DialogTitle>
          <DialogDescription>
            Complete los campos. Los campos calculados se actualizarán automáticamente.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Código de Cotización *</Label>
              <Input
                value={formData.quotation_code}
                onChange={(e) => handleChange('quotation_code', e.target.value)}
                placeholder="MV-0010624"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Nombre del Proyecto *</Label>
              <Input
                value={formData.project_name}
                onChange={(e) => handleChange('project_name', e.target.value)}
                placeholder="AFFINITY"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Total M²</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.total_m2}
                onChange={(e) => handleChange('total_m2', e.target.value)}
                placeholder="1500.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>FOB Real (Cotización Fábrica)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.fob_real}
                onChange={(e) => handleChange('fob_real', e.target.value)}
                placeholder="80000.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Cantidad de Contenedores</Label>
              <Input
                type="number"
                value={formData.container_count}
                onChange={(e) => handleChange('container_count', e.target.value)}
                placeholder="3"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>FOB Colocado en Excel</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.fob_excel}
                onChange={(e) => handleChange('fob_excel', e.target.value)}
                placeholder="85000.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Venta (Inc. IGV, Suministro)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.sale_with_igv}
                onChange={(e) => handleChange('sale_with_igv', e.target.value)}
                placeholder="195000.00"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Flete</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.freight_cost}
                onChange={(e) => handleChange('freight_cost', e.target.value)}
                placeholder="3500.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Nacionalización</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.nationalization_cost}
                onChange={(e) => handleChange('nationalization_cost', e.target.value)}
                placeholder="45000.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Logístico Interno</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.logistics_cost}
                onChange={(e) => handleChange('logistics_cost', e.target.value)}
                placeholder="2500.00"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Stats Modal Component
 */
const StatsModal = ({ isOpen, onClose, stats }) => {
  if (!stats) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Estadísticas Históricas</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-600 uppercase">Total Registros</p>
              <p className="text-2xl font-bold text-blue-700">{stats.total_entries}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-xs text-green-600 uppercase">Proyectos Únicos</p>
              <p className="text-2xl font-bold text-green-700">{stats.unique_projects}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-xs text-purple-600 uppercase">Total M²</p>
              <p className="text-2xl font-bold text-purple-700">{stats.total_m2?.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-xs text-orange-600 uppercase">Total Contenedores</p>
              <p className="text-2xl font-bold text-orange-700">{stats.total_containers}</p>
            </div>
          </div>
          
          {/* M2 range */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-medium mb-2">Rango de M²</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Mínimo:</span>
                <span className="ml-2 font-medium">{stats.min_m2?.toLocaleString()} M²</span>
              </div>
              <div>
                <span className="text-slate-500">Promedio:</span>
                <span className="ml-2 font-medium">{stats.avg_m2?.toLocaleString()} M²</span>
              </div>
              <div>
                <span className="text-slate-500">Máximo:</span>
                <span className="ml-2 font-medium">{stats.max_m2?.toLocaleString()} M²</span>
              </div>
            </div>
          </div>
          
          {/* Container distribution */}
          {stats.container_by_m2_range && stats.container_by_m2_range.length > 0 && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Ship className="w-4 h-4" />
                Contenedores por Rango de M²
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="pb-2">Rango M²</th>
                      <th className="pb-2 text-center">Proyectos</th>
                      <th className="pb-2 text-center">Min Cont.</th>
                      <th className="pb-2 text-center">Promedio</th>
                      <th className="pb-2 text-center">Max Cont.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.container_by_m2_range.map((range) => (
                      <tr key={range._id} className="border-b border-slate-100">
                        <td className="py-2 font-medium">{range._id}</td>
                        <td className="py-2 text-center">{range.count}</td>
                        <td className="py-2 text-center text-blue-600">{range.min_containers}</td>
                        <td className="py-2 text-center font-medium">{range.avg_containers?.toFixed(1)}</td>
                        <td className="py-2 text-center text-blue-600">{range.max_containers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                💡 Usa esta información para proyectar cuántos contenedores necesitarás según los M² del proyecto.
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HistoricalView;
